import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { brand } from "../../config/theme";
import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";
import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { useActivosMenu } from "./hooks/useActivosMenu";
import { ACTIVOS_APP_ID, ACTIVOS_BASE } from "./paths";

// ============================================================================
// Shell de la app «Activos» (épica común CC-CONSOLE ↔ WAYAK, #1638). Archivo
// común, idéntico en los dos forks: espeja ExpensesLayout (también idéntico).
//
// Sub-menú: cada ítem se pinta si el preset del tenant lo trae
// (`isMenuItemEnabled`) Y la sesión tiene su menú (`menu:activos.<id>`). La
// compañía la decide el servidor (no hay selector): el API ignora
// `x-company-id`.
// ============================================================================
export default function ActivosLayout() {
  const visibleMenu = useActivosMenu();
  const { t } = useTranslation("activos");
  const { t: tc } = useTranslation("common");
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon || app.isLocked) {
      Swal.fire({
        title: app.isComingSoon ? tc("appsSidebar.comingSoon") : tc("appsSidebar.premiumFeature"),
        text: app.isComingSoon ? tc("appsSidebar.comingSoonText", { name: app.name }) : tc("appsSidebar.premiumRequired", { name: app.name }),
        icon: app.isComingSoon ? "info" : "warning",
        confirmButtonText: tc("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      setIsAppsSidebarOpen(false);
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = visibleMenu.map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${ACTIVOS_BASE}/${item.id}`,
  }));

  return (
    <div className="flex h-[100dvh] pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="flex items-center justify-center gap-2 bg-warning px-4 py-2 text-center text-sm text-white">
          <i className="ri-alarm-warning-line" />
          <span>{tc("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header
        title={t("appName")}
        subtitle={t("appSubtitle")}
        onOpenAppsSidebar={() => setIsAppsSidebarOpen(true)}
        onToggleMobileMenu={() => setIsMobileMenuOpen((p) => !p)}
        isMobileMenuOpen={isMobileMenuOpen}
      />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname === `${ACTIVOS_BASE}/${itemId}` || location.pathname.startsWith(`${ACTIVOS_BASE}/${itemId}/`)}
          footerStats={{
            label: t("appName"),
            items: [{ label: t("menu.sections"), value: menuItems.length }],
          }}
        />
        <div className="flex min-w-0 flex-1 justify-center overflow-hidden">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={() => setIsAppsSidebarOpen(false)}
        categories={filteredCategories}
        selectedAppId={ACTIVOS_APP_ID}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? { first_name: user.first_name, last_name: user.last_name, email: user.email, username: user.username } : undefined}
        onProfileClick={() => {
          setIsAppsSidebarOpen(false);
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
